import { useCallback, useEffect, useRef, useState } from 'react';

import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { AnalysisRun, useVendorAnalysisApi } from '../../../common/hooks/use-vendor-analysis-api.hook';
import { useCommerceApi } from '../../../common/hooks/use-commerce-api.hook';
import { Notebook, applyAnalysisRun, applyDetection, applyDiscoveryEvent, emptyNotebook } from './journey';
import { playPreview } from './journey-preview';

const POLL_MS = 1500;

/**
 * Ardi reading the business, while the vendor answers questions.
 *
 * Three readers run side by side once there is a website: brand discovery
 * (streamed — palette, fonts, voice, the social handles the site links to),
 * the search-market read (polled — the same run the Business pages show),
 * and the shop detector (one call — which platform the site runs on). All
 * three fold into one notebook the screen draws.
 *
 * The runs are server-side: leaving the journey does not stop them, and the
 * Brand and Business pages show their results whenever they land.
 */
export function useBusinessLearning({ preview, instant }: { preview: boolean; instant: boolean }) {
    const studio = useContentStudioApi();
    const analysis = useVendorAnalysisApi();
    const commerce = useCommerceApi();

    const [notebook, setNotebook] = useState<Notebook>(emptyNotebook());
    const [run, setRun] = useState<AnalysisRun | null>(null);
    const [started, setStarted] = useState(false);
    const stream = useRef<AbortController | null>(null);
    const stopPreview = useRef<(() => void) | null>(null);

    const onDiscovery = useCallback((e: { type: string; data: any }) => setNotebook((nb) => applyDiscoveryEvent(nb, e)), []);
    const onRun = useCallback((r: AnalysisRun) => {
        setRun(r);
        setNotebook((nb) => applyAnalysisRun(nb, r));
    }, []);

    /** Begin reading `website` (already saved on the profile by the caller). */
    const start = useCallback(
        async (website: string, { brand = true }: { brand?: boolean } = {}) => {
            setStarted(true);
            setNotebook((nb) => ({ ...emptyNotebook(website), socials: nb.socials }));
            if (preview) {
                stopPreview.current?.();
                stopPreview.current = playPreview({ onDiscovery, onRun, onDetection: (d) => setNotebook((nb) => applyDetection(nb, d)) }, instant);
                return;
            }
            if (brand) {
                stream.current?.abort();
                stream.current = studio.discoverBrandStream({ websiteUrl: website }, onDiscovery);
            }
            // The profile write already started a read of a NEW website; start()
            // returns that live run rather than buying a second one.
            analysis
                .start()
                .then(onRun)
                .catch(() => analysis.latest().then((r) => r && onRun(r)).catch(() => undefined));
            commerce
                .detect()
                .then((d) => setNotebook((nb) => applyDetection(nb, d)))
                .catch(() => undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [preview, instant],
    );

    // Join a run already going (a refresh mid-journey) instead of starting one.
    const resume = useCallback(
        async (website: string) => {
            setStarted(true);
            setNotebook((nb) => ({ ...nb, website }));
            if (preview) return start(website);
            analysis.latest().then((r) => r && onRun(r)).catch(() => undefined);
            commerce.detect().then((d) => setNotebook((nb) => applyDetection(nb, d))).catch(() => undefined);
            // The brand kit already read (or being read) is on the server; show it.
            studio
                .getBrandKit()
                .then((kit: any) => kit && onDiscovery({ type: 'tool_result', data: { name: 'brand_kit_updated', brandKit: kit, report: {} } }))
                .catch(() => undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [preview, start],
    );

    // Poll the search read only while it runs.
    useEffect(() => {
        if (preview || !run || (run.status !== 'pending' && run.status !== 'running')) return undefined;
        const id = window.setInterval(async () => {
            try {
                onRun(await analysis.get(run.runId));
            } catch {
                /* a dropped poll is not a failed run */
            }
        }, POLL_MS);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run?.runId, run?.status, preview]);

    useEffect(
        () => () => {
            // Leaving the screen stops listening, not the work: the server keeps reading.
            stream.current?.abort();
            stopPreview.current?.();
        },
        [],
    );

    return { notebook, setNotebook, run, started, start, resume };
}
